import { useState } from "react";
import { Button } from "@/components/ui/button";
import { DataTable, type DataTableColumn } from "@/components/ui/DataTable/index";
import { Combobox } from "@/components/ui/combobox";
import { DatePicker } from "@/components/ui/date_picker";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  useListInstances,
  useListTasks,
  TaskInstance,
  InstanceStatus,
  SecondaryInstanceStatus,
  Task,
} from "@/lib/api/checklist";
import { PriorityBadge, StatusIndicator } from "./components";
import { format } from "date-fns";
import { CommonDestinationTimezone } from "@/lib/utils";
import moment from "moment-timezone";
import { useQuery } from "@tanstack/react-query";
import { fetchApiGet } from "@/lib/api/utils";
import { DbUser } from "@/lib/models";
import { Eye, CheckCircle, ArrowRightLeftIcon, SkipForwardIcon } from "lucide-react";
import { useListMinimalUsers } from "@/lib/api/users";

interface InstanceListProps {
  onCompleteInstance: (instance: TaskInstance) => void;
  onNonApplInstance: (instance: TaskInstance) => void;
  onReassignInstance: (instance: TaskInstance) => void;
  onViewTask: (taskId: string) => void;
}

interface UsersResponse {
  data: DbUser[];
}

const statusOptions = [
  { value: "", label: "All Statuses" },
  { value: InstanceStatus.PENDING, label: "Pending" },
  { value: InstanceStatus.COMPLETED, label: "Completed" },
    /* { value: InstanceStatus.CANCELLED, label: "Cancelled" }, */
];

export function InstanceList({
    onCompleteInstance,
    onNonApplInstance,
  onReassignInstance,
    onViewTask,
}: InstanceListProps) {
    const [page, setPage] = useState(1);
  const [status, setStatus] = useState<InstanceStatus | "">(InstanceStatus.PENDING);
  const [assigneeId, setAssigneeId] = useState("");
  const [taskId, setTaskId] = useState("");
  const [isLate, setIsLate] = useState(false);
  const [endDate, setEndDate] = useState<Date | undefined>();

  const limit = 1000;

  const minimalUserListResult = useListMinimalUsers();
  const users = (minimalUserListResult?.data?.data || []);

  const tasksResult = useListTasks({ limit: 1000 });
  const tasks = tasksResult?.data?.data?.data || [];

  const instancesResult = useListInstances({
    page,
    limit,
    status: status || undefined,
    assigned_to_id: assigneeId || undefined,
    task_id: taskId || undefined,
    is_late: isLate || undefined,
    due_date_to: endDate?.toISOString(),
  });
  
  const isLoading = instancesResult.isLoading;
  const instances = instancesResult?.data?.data?.instances || [];
  const total = instancesResult?.data?.data?.total || 0;

  const userOptions = [
    { value: "", label: "All Users" },
    ...users.map((user: DbUser) => ({
      value: user.id,
      label: user.name,
    })),
  ];

  const taskOptions = [
    { value: "", label: "All Tasks" },
    ...tasks.map((task: Task) => ({
      value: task.id,
      label: task.title,
    })),
  ];

  const formatDate = (dateString: string) => {
    return moment.tz(dateString, CommonDestinationTimezone).format("MMM DD, YYYY");
  };

  const columns: DataTableColumn<TaskInstance>[] = [
    {
      accessorKey: "task.title",
      header: "Task",
      cell: ({ row }) => {
        const instance = row.original;
        return (
          <div>
            <button
              onClick={() => onViewTask(instance.task_id)}
              className="text-left font-medium hover:underline"
            >
              {instance.task?.title}
            </button>
            {instance.task?.priority && (
              <div className="mt-1">
                <PriorityBadge priority={instance.task.priority} />
              </div>
            )}
            {instance.template_instance?.template?.name && (
              <div className="text-xs text-muted-foreground">
                {instance.template_instance?.template?.name}
              </div>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "assigned_to",
      header: "Assignee",
      cell: ({ row }) => {
        const instance = row.original;
        return instance.assigned_to
          ? instance.assigned_to.name
          : "Unassigned";
      },
      size: 150,
    },
    {
      accessorKey: "due_date",
      header: "Due Date",
      cell: ({ row }) => {
        const instance = row.original;
        return (
          <div>
            <span>{formatDate(instance.due_date)}</span>
            {instance.is_late && instance.status === InstanceStatus.PENDING && (
              <span className="ml-2 text-destructive text-sm font-medium">
                Late
              </span>
            )}
            {instance.is_late && instance.status === InstanceStatus.COMPLETED && (
              <span className="ml-2 text-destructive text-sm font-medium">
                {instance.secondary_status === SecondaryInstanceStatus.NonAppl ? "Non-Appl Late" : "Completed Late"}
              </span>
            )}
          </div>
        );
      },
      size: 120,
    },
    {
      accessorKey: "status",
      header: "Status",
      accessorFn: (row) => row.secondary_status === SecondaryInstanceStatus.NonAppl ? "Non-Appl" : row.status,
      cell: (info) => <StatusIndicator status={info.row.original.status} secondary_status={info.row.original.secondary_status}/>,
      size: 100,
    },
    {
      accessorKey: "completed_at",
      header: "Completed",
      cell: ({ row }) => {
        const instance = row.original;
        return instance.completed_at ? formatDate(instance.completed_at) : "-";
      },
      size: 120,
    },
    {
      accessorKey: "template_instance",
      header: "Template Info",
      cell: ({ row }) => {
        const instance = row.original;
        return instance.template_instance?.trigger_meta_data?.customer_name ?? "-";
      },
      size: 120,
    },
    {
      accessorKey: "triggered_by_user",
      header: "Triggered By",
      accessorFn: (row: any) => row.template_instance?.triggered_by_user?.name,
      cell: (info) => {
        return info.getValue() ?? "-";
      },
      size: 120,
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const instance = row.original;
        const isPending = instance.status === InstanceStatus.PENDING;

        return (
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onViewTask(instance.task_id)}
              title="View Task"
            >
              <Eye className="h-4 w-4" />
            </Button>
            {isPending && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onNonApplInstance(instance)}
                  title="Non Appl"
                >
                  <SkipForwardIcon className="h-4 w-4 mr-1" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onCompleteInstance(instance)}
                  title="Complete"
                >
                  <CheckCircle className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onReassignInstance(instance)}
                  title="Reassign"
                >
                  <ArrowRightLeftIcon className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
        );
      },
      size: 150,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="grid space-y-2">
            <Label>Status</Label>
            <Combobox
              value={status}
              onValueChange={(value) => setStatus(value as InstanceStatus | "")}
              options={statusOptions}
              placeholder="Select status"
              dwimOnChange
            />
          </div>
          <div className="grid space-y-2">
            <Label>Assignee</Label>
            <Combobox
              value={assigneeId}
              onValueChange={setAssigneeId}
              options={userOptions}
              placeholder="Select assignee"
              dwimOnChange
            />
          </div>
          <div className="flex items-end">
            <div className="flex items-center space-x-2">
              <Switch
                id="is-late"
                checked={isLate}
                onCheckedChange={setIsLate}
              />
              <Label htmlFor="is-late">Only show late</Label>
            </div>
          </div>
          <div>
          <Label>End Date</Label>
          <DatePicker
            value={endDate}
            onChange={setEndDate}
            placeholder="Filter by end date"
            buttonClassName="w-full"
          />
        </div>
      </div>
    </div>

      <DataTable
        defaultSorting={[{ id: "due_date", desc: false }, { id: "completed_at", desc: false }]}
        data={instances}
        columns={columns}
        stickyHeaders
        enableSorting
        enableMultiSort
        enableFiltering
        filterStyle='select'
        filterMode='internal'
        emptyMessage={isLoading ? "Loading..." : "No data available"}
        tableClassName={"min-h-[600px] max-h-[70vh] overflow-y-auto"}
      />

      {total > limit && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {(page - 1) * limit + 1} to{" "}
            {Math.min(page * limit, total)} of {total} instances
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={page * limit >= total}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
